import { FC, useCallback, useEffect, useMemo } from 'react';
import { Button, Dropdown, MenuProps, message, Modal } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  selectMultipleResultsTransaction,
  deselectMultipleResultsTransaction,
  selectedResultsAtom,
} from 'packages/omnitag/state';
import { allSearchResultsAtom } from 'packages/omnitag/state/text-search';
import { isMacDevice } from 'packages/instance-seg/components/shortcut-controls/constants';
import { useResetAtom } from 'jotai/utils';
import { useTourRef } from 'packages/omnitag/hooks/use-tour-refs';

// Oct-17-2025 - Used Gemini 2.5 Agent to assist with adding keyboard shortcuts and Deselect All

export enum SelectOptions {
  'add-to-selection' = 'add-to-selection',
  'remove-from-selection' = 'remove-from-selection',
  'deselect-all' = 'deselect-all',
}

export const SelectDropdown: FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const results = useAtomValue(allSearchResultsAtom);
  const isLoading = !results || results.isLoading || results.isValidating;
  const resetSelectedResults = useResetAtom(selectedResultsAtom);
  const selectMultipleResults = useSetAtom(selectMultipleResultsTransaction);
  const deselectMultipleResults = useSetAtom(
    deselectMultipleResultsTransaction
  );
  const modifySelectionRef = useTourRef<HTMLButtonElement>(
    'modify-selection-dropdown'
  );

  const data = results?.data?.imageMetadata;
  const loadedResultsData = data && Array.isArray(data);

  const onSelectItemClick: MenuProps['onClick'] = (item) => {
    if (item.key === SelectOptions['add-to-selection']) {
      void selectAllCurrentPage();
    } else if (item.key === SelectOptions['remove-from-selection']) {
      void deselectAllCurrentPage();
    } else if (item.key === SelectOptions['deselect-all']) {
      Modal.confirm({
        title: 'Deselect All',
        content: 'Are you sure you want to deselect all items?',
        onOk() {
          resetSelectedResults();
        },
      });
    }
  };

  const selectAllCurrentPage = useCallback(() => {
    if (loadedResultsData) {
      selectMultipleResults(data);
      void messageApi.open({
        type: 'success',
        content: `Added ${data.length} items to selection`,
      });
    }
  }, [data, loadedResultsData, messageApi, selectMultipleResults]);

  const deselectAllCurrentPage = useCallback(() => {
    if (loadedResultsData) {
      deselectMultipleResults(data);
      void messageApi.open({
        type: 'success',
        content: `Removed ${data.length} items from selection`,
      });
    }
  }, [data, loadedResultsData, messageApi, deselectMultipleResults]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((isMacDevice && event.metaKey) || (!isMacDevice && event.ctrlKey)) {
        if (event.key === 'a') {
          event.preventDefault();
          if (event.shiftKey) {
            void deselectAllCurrentPage();
          } else {
            void selectAllCurrentPage();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectAllCurrentPage, deselectAllCurrentPage]);

  const selectDropdownItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: SelectOptions['add-to-selection'],
        label: 'Add Current Page to Selection ',
        extra: isMacDevice ? '⌘-A' : 'Ctrl-A',
      },
      {
        key: SelectOptions['remove-from-selection'],
        label: 'Remove Current Page from Selection',
        extra: isMacDevice ? '⌘-Shift-A' : 'Ctrl-Shift-A',
      },
      {
        key: SelectOptions['deselect-all'],
        label: 'Deselect All...',
      },
    ],
    []
  );

  return (
    <>
      {contextHolder}

      <Dropdown
        menu={{ items: selectDropdownItems, onClick: onSelectItemClick }}
        placement="bottom"
        disabled={isLoading}
      >
        <Button ref={modifySelectionRef}>
          Modify Selection <DownOutlined />
        </Button>
      </Dropdown>
    </>
  );
};
